import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { filterNil } from '@ngneat/elf';
import { TranslateService } from '@ngx-translate/core';
import { Mutex } from 'async-mutex';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  merge,
  of,
  pairwise,
  Subscription,
  take,
  tap,
  timeout,
} from 'rxjs';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import {
  SpeedbumpModalComponent,
  SpeedbumpModalData,
} from 'src/app/dialogs/speedbump-modal/speedbump-modal.component';
import { Context } from 'src/app/models/context';
import { RealtimeDataUpdate } from 'src/app/models/realtime';
import { ISession, Session, SessionUser } from 'src/app/models/session';
import { User } from 'src/app/models/user';
import { AclService } from 'src/app/services/acl.service';
import { MessagingService } from 'src/app/services/messaging.service';
import { MessengerService } from 'src/app/services/messenger.service';
import { PresenceProvider } from 'src/app/services/presence-provider';
import { RealtimeService } from 'src/app/services/realtime.service';
import { RtcServiceController } from 'src/app/services/rtc.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { SpacesTranscriptionsService } from 'src/app/services/space-transcription.service';
import { SpacesService } from 'src/app/services/spaces.service';
import { HomeNavigationService } from 'src/app/services/home-navigation.service';
import { SpaceNavigationService } from 'src/app/services/space-navigation.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { UserService } from 'src/app/services/user.service';
import {
  MessageDuration,
  ToastMessageTarget,
  WbToastMessageService,
} from 'src/app/services/wb-toast-message.service';
import { ISpaceUI, SpaceRepository } from 'src/app/state/space.repository';
import { TranscriptRepository } from 'src/app/state/transcript.repository';
import { ChatOptions } from 'src/app/communication/chat/chat.component';
import { TypedMessage } from 'src/app/models/messaging';
import { debounce } from 'lodash';
import { ERRORS } from 'src/app/common/utils/notification-constants';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { AudioChimes, AudioService } from 'src/app/services/audio.service';
import {
  NotificationIconTypes,
  NotificationToasterService,
} from 'src/app/services/notification-toaster.service';
import { SpinnerModalComponent } from 'src/app/standalones/components/modals/spinner-modal/spinner-modal.component';
import { Collaboration as CommonCollaboration } from '@pncl/common-models';
import { PanelView } from '../../panel/panel.component';

enum RealTimeActionEnum {
  EndSession = 'endSession',
  UpdateData = 'updateData',
  BroadcastTranscription = 'broadcastTranscript',
  SpaceTranscriptStatusUpdate = 'spaceTranscriptStatusUpdate',
  SpaceTranscriptServiceTypeUpdate = 'spaceTranscriptServiceTypeUpdate',
}

enum RealtimeUpdateDataType {
  USER = 'user',
  USER_JOINED = 'user_joined',
  VISIBILITY = 'visibility',
  LOCKED = 'islocked',
  TITLE = 'title',
  SESSION_PERMISSIONS = 'sessionpermissions',
  SESSION_DEFAULT_FRAME_BACKGROUND = 'sessiondefaultframesbackground',
  SETTINGS = 'settings',
  USER_SPACE_TRANSFER = 'user_space_transfer',
}

@Injectable({
  providedIn: 'root',
})
export class InSpaceRealtimeHandlerService {
  private realtimeUserMutex = new Mutex();
  private releaseRealtimeMutex: any;
  private isChimesEnabled = this.flagsService.isFlagEnabled(FLAGS.SPACES_USER_CHIMES);

  private sessionChannelSub?: Subscription;
  private endSessionSub?: Subscription;
  private messagesChannelSub?: Subscription;
  private messageReceivedSub?: Subscription;
  private roomDeletionSub?: Subscription;
  private roomChangeChimeSub?: Subscription;

  private movedFromRoomID: string = '';

  constructor(
    private spaceRepo: SpaceRepository,
    private sessionSharedDataService: SessionSharedDataService,
    private realtimeService: RealtimeService,
    private presenceProvider: PresenceProvider,
    private spacesTranscriptionsService: SpacesTranscriptionsService,
    private transcriptRepo: TranscriptRepository,
    private dialog: MatDialog,
    private toastMessageService: WbToastMessageService,
    private translate: TranslateService,
    private rtcServiceController: RtcServiceController,
    private userService: UserService,
    private router: Router,
    private spacesService: SpacesService,
    private aclService: AclService,
    private telemetry: TelemetryService,
    private messagingService: MessagingService,
    private messengerService: MessengerService,
    private flagsService: FlagsService,
    private audioService: AudioService,
    private notificationToasterService: NotificationToasterService,
    private spaceNavigationService: SpaceNavigationService,
    private homeNavigation: HomeNavigationService,
  ) {
    this.flagsService.featureFlagChanged(FLAGS.SPACES_USER_CHIMES).subscribe((val) => {
      this.isChimesEnabled = val;
    });
  }

  public initialize(spaceID: string): void {
    this.loadSessionAuthAndSetupRT(spaceID);
    this.initializeChatListeners();
    this.setupListenersForRoomDeletion();
    this.setupRoomChangeChimeListener();
  }

  private loadSessionAuthAndSetupRT(spaceID: string): void {
    const space = this.spaceRepo.getSpace(spaceID);
    if (!space?._id || !space.metadata) {
      this.telemetry.errorEvent('[realtime-space-metadata-undefined]');
      this.homeNavigation.navigate().then((navigationSuccess) => {
        if (navigationSuccess) {
          this.notificationToasterService.showDefaultErrorNotificationV2(
            this.translate.instant('Something went wrong'),
            this.translate.instant('Please try joining your space again.'),
            ERRORS.SPACE_METADATA_UNDEFINED,
          );
        }
      });
      return;
    }

    const authObj: Partial<Session> = {
      _id: space._id,
      metadata: space.metadata,
    };
    this.sessionSharedDataService.populateData([authObj]);
    this.setupRealtime(spaceID);
  }

  private setupRealtime(spaceID?: string) {
    if (!spaceID) {
      this.telemetry.event('[realtime-spaceID-undefined]');
      return;
    }

    const authData = this.sessionSharedDataService.sessionAuthData[spaceID];
    const realtimeToken = authData?.realtimeToken;
    if (!realtimeToken) {
      this.telemetry.event('[realtime-token-undefined]');
      return;
    }

    this.sessionChannelSub = this.realtimeService
      .subscribeSession(spaceID, realtimeToken)
      .pipe(filterNil())
      .subscribe((message) => {
        this.handleRealtimeMessage(message);
      });
  }

  public cleanupRealtime(oldSessionId?: string) {
    this.endSessionSub?.unsubscribe();
    this.sessionChannelSub?.unsubscribe();
    this.messagesChannelSub?.unsubscribe();
    this.messageReceivedSub?.unsubscribe();
    this.roomDeletionSub?.unsubscribe();
    this.roomChangeChimeSub?.unsubscribe();

    this.movedFromRoomID = '';

    // On an in-place space switch the active space is already reset by the time this runs, so the
    // caller passes the old space id explicitly; fall back to the active space id (destroy path).
    const sessionId = oldSessionId ?? this.spaceRepo.activeSpaceId ?? '';
    if (this.sessionChannelSub) {
      this.sessionChannelSub = undefined;
      this.realtimeService.unsubscribeSession(sessionId);
    }
  }

  private handleRealtimeMessage(update: RealtimeDataUpdate) {
    const sessionId = update.sessionId || update.id;
    const isThisSession = sessionId === this.spaceRepo.activeSpaceId;

    const { activeUsers, inactiveUsers } = this.presenceProvider.getSpaceUsersActivityStatus(
      this.spaceRepo.activeSpaceId as string,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );

    switch (update.action) {
      case RealTimeActionEnum.EndSession:
        if (isThisSession) {
          this.endSession();
        }
        break;

      case RealTimeActionEnum.UpdateData:
        this.handleDataUpdate(update);
        break;

      case RealTimeActionEnum.BroadcastTranscription:
        this.spacesTranscriptionsService.handleSpaceTranscriptEvent(
          update.data,
          update.operation as string,
          activeUsers,
          inactiveUsers,
        );
        break;

      case RealTimeActionEnum.SpaceTranscriptStatusUpdate:
        this.transcriptRepo.updateSpaceTranscriptionStatus(update.data.status);
        break;

      case RealTimeActionEnum.SpaceTranscriptServiceTypeUpdate:
        this.spacesTranscriptionsService.onServiceTypeChange(update.data.service);
        break;
    }
  }

  private endSession(sessionId?: string): void {
    if (!this.user) {
      // Should not happen, but technically this.user could be null.
      this.router.navigate(['/'], {});
      return;
    }

    if (this.aclService.isGuest(this.user)) {
      // Guest users will go to Home.
      this.homeNavigation.navigate();
      return;
    }

    if (this.spaceRepo.isCurrentUserHost()) {
      sessionId = sessionId ?? this.spaceRepo.activeSpace?._id;
      if (sessionId) {
        this.endSessionSub = this.spacesService.endSession(sessionId).subscribe();
        // Check if there are other active sessions
        this.sessionSharedDataService.sessionEnded.next(sessionId);
      }
    } else {
      this.homeNavigation.navigate();
    }
  }

  private async handleDataUpdate(update: RealtimeDataUpdate) {
    if (update.data?.intendedFor && !this.isIntendedForCurrentUser(update.data.intendedFor)) {
      return;
    }

    switch (update.dataType.toLowerCase()) {
      case RealtimeUpdateDataType.USER:
        const [currentUserUpdated, roomChanged] = this.roomChangedForCurrentUser(
          this.spaceRepo.activeSpace,
          update,
        );
        if (currentUserUpdated && roomChanged && this.realtimeUserMutex.isLocked()) {
          this.releaseRealtimeMutex();
        } else if (currentUserUpdated && !roomChanged) {
          this.dialog.openDialogs
            .find((d) => d.componentInstance instanceof SpeedbumpModalComponent)
            ?.close();
        }
        this.releaseRealtimeMutex = await this.realtimeUserMutex.acquire();
        await this.handleUserDataUpdate(update);
        this.releaseRealtimeMutex();
        break;
      case RealtimeUpdateDataType.USER_JOINED:
        this.handleUserJoined(update);
        break;
      case RealtimeUpdateDataType.VISIBILITY:
        this.handleVisibilityUpdate(update);
        break;
      case RealtimeUpdateDataType.LOCKED:
        this.handleLockStateUpdate(update);
        break;
      case RealtimeUpdateDataType.TITLE:
        this.handleTitleUpdate(update);
        break;
      case RealtimeUpdateDataType.SESSION_PERMISSIONS:
        this.handleSessionPermissions(update);
        break;
      case RealtimeUpdateDataType.SESSION_DEFAULT_FRAME_BACKGROUND:
        this.handleSessionDefaultBackgroundUpdate(update);
        break;
      case RealtimeUpdateDataType.SETTINGS:
        this.handleSpaceSettingsUpdate(update);
        break;
      case RealtimeUpdateDataType.USER_SPACE_TRANSFER:
        this.handleUserSpaceTransfer(update);
        break;
    }
  }

  /**
   * handling the new incoming space default backgrounds for frames
   * @param update
   * @private
   */
  private handleSessionDefaultBackgroundUpdate(update: RealtimeDataUpdate): void {
    const sessionId = update.id;
    const sessionDefaultFramesBackground = update.data.sessionDefaultFramesBackground;
    const session = this.spaceRepo.getSpace(sessionId);
    if (session) {
      session.sessionDefaultFramesBackground = sessionDefaultFramesBackground;
      this.spaceRepo.updateSpace(sessionId, session);
    }
  }

  private handleSessionPermissions(update: RealtimeDataUpdate): void {
    const sessionId = update.id;
    const newPermissions = update.data.sessionPermissions;
    const session = this.spaceRepo.getSpace(sessionId);
    if (session) {
      session.sessionPermissions = newPermissions;
      this.updateParticipantsPermissions(session, newPermissions);
      this.spaceRepo.updateSpace(sessionId, session);
    }
  }

  private updateParticipantsPermissions(session: ISession, newPermissions: Permissions): void {
    session?.users.forEach((user) => {
      if (!user.isOwner) {
        user.userPermissions = { ...user.userPermissions, ...newPermissions };
      }
    });
  }

  private handleUserJoined(update: RealtimeDataUpdate): void {
    const sessionId = update.id;

    const session = this.spaceRepo.getSpace(sessionId);
    if (session) {
      const userIndex = session.users.findIndex(
        (sessionUser) => sessionUser._id === update.data.userId,
      );
      if (userIndex >= 0) {
        session.users[userIndex].joined = true;
        this.spaceRepo.updateSpace(sessionId, session);
      }
    }
  }

  private handleLockStateUpdate(update: RealtimeDataUpdate) {
    const sessionId = update.id;
    if (!sessionId) {
      return;
    }

    const isLocked = update.data.isLocked;
    const session = this.spaceRepo.getSpace(sessionId);
    if (session) {
      session.isLocked = isLocked;
      this.spaceRepo.updateSpace(sessionId, session);
    }

    if (isLocked && !Session.isOwnedByUser(session, this.user)) {
      this.showSpaceLockedNotification();
    }
  }

  private showSpaceLockedNotification(): void {
    this.toastMessageService.show({
      message: 'Space locked by the host. You can only move around the board.',
      icon: 'lock',
      duration: MessageDuration.Long,
      target: ToastMessageTarget.WB_Toolbar,
    });
  }

  private handleVisibilityUpdate(update: RealtimeDataUpdate) {
    const sessionId = update.id;

    const visibility = update.data.visibility;

    const session = this.spaceRepo.getSpace(sessionId);
    if (session) {
      session.visibility = visibility;
      this.spaceRepo.updateSpace(sessionId, session);
    }
  }

  private handleTitleUpdate(update: RealtimeDataUpdate) {
    const sessionId = update.id;
    const title = update.data.title;
    const session = this.spaceRepo.getSpace(sessionId);
    if (session) {
      session.title = title;
      this.spaceRepo.updateSpace(sessionId, session);
    }
  }

  private roomChangedForCurrentUser(
    currentSession: (ISession & ISpaceUI) | undefined,
    updatedSession: RealtimeDataUpdate,
  ) {
    const currentRoomId = currentSession?.users.find((u) => u._id === this.user?._id)?.roomId;
    const currentUserUpdated = updatedSession.data.sessionUsers?.find(
      (u: SessionUser) => u._id === this.user?._id,
    );
    const toJoinRoomId = currentUserUpdated?.roomId;
    return [!!currentUserUpdated, currentRoomId && toJoinRoomId && currentRoomId !== toJoinRoomId];
  }

  private async handleUserDataUpdate(update: RealtimeDataUpdate) {
    const sessionId = update.id;

    if (!sessionId) {
      return;
    }

    // If the current user gets removed from a deleted space redirect them back to the spaces landing page
    if (
      this.user &&
      update.data.addUpdateUsers.length === 0 &&
      update.data.removeUsers.length > 0 &&
      update.data.removeUsers.find((removedUser: User) => removedUser._id === this.user?._id)
    ) {
      this.spaceNavigationService.leaveToHome({ force: true });
      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translate.instant('Space deleted'),
        this.translate.instant('This Space has been deleted and is no longer accessible.'),
        ERRORS.SPACE_DELETED,
      );
    } else if (
      /* If the current user gets removed from the session, they are redirected to
       * the home page. Attempting to rejoin the session will give them an access
       * denied error.
       */
      this.user &&
      update.data.removeUsers.find((removedUser: User) => removedUser._id === this.user?._id)
    ) {
      if (this.rtcServiceController.service.isConnected()) {
        await this.rtcServiceController.service.leave();
      }
      this.spaceNavigationService.leaveToHome({ force: true });
      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translate.instant('Access removed'),
        this.translate.instant('You have been removed from this Space by the host.'),
        ERRORS.ACCESS_REVOKED,
      );
    }

    const session = this.spaceRepo.getSpace(sessionId);
    const confirmJoinNewRoom = await this.handleRoomChangedUpdate(sessionId, update);

    if (session && confirmJoinNewRoom) {
      const updatedSession = this.updateSessionUsers(session, update);
      this.spaceRepo.updateSpace(sessionId, updatedSession);
    }
  }

  private updateSessionUsers(session: ISession, update: RealtimeDataUpdate): Session {
    const updatedSession = new Session(session);

    // Update the context
    const context: Context = {};
    const sessionUserIds = updatedSession?.context?.users || [];
    const updatedUserIds = update.data.addUpdateUsers.map((u: User) => u._id);
    context.users = Array.from(
      new Set(
        [...sessionUserIds, ...updatedUserIds].filter(
          /* After merging existing users and added/updated users, filter out users
           * that were removed.
           */
          (userId) =>
            !update.data.removeUsers.find((removedUser: User) => removedUser._id === userId),
        ),
      ),
    );
    updatedSession.context = context;

    // Update the users
    update.data.addUpdateUsers.forEach((sessionUser: SessionUser) => {
      const index = updatedSession.users.findIndex((u) => u._id === sessionUser._id);
      if (index >= 0) {
        updatedSession.users[index] = sessionUser;
      } else {
        updatedSession.users.push(sessionUser);
      }
    });

    /* After merging existing users and added/updated users, filter out users
     * that were removed.
     */
    updatedSession.users = updatedSession.users.filter(
      (sessionUser) =>
        !update.data.removeUsers.find((removedUser: User) => removedUser._id === sessionUser._id),
    );

    // Update the populated users
    update.data.userObjects?.forEach((userObject: User) => {
      const index = updatedSession.populatedUsers?.findIndex((u) => u._id === userObject._id);
      if (index !== undefined && index >= 0) {
        updatedSession.populatedUsers[index] = userObject;
      } else {
        updatedSession.populatedUsers.push(userObject);
      }
    });

    return updatedSession;
  }

  private async handleRoomChangedUpdate(sessionId: string, update: RealtimeDataUpdate) {
    let applySessionUpdates = true;
    const currentRoomDeleted = this.isRoomDeleted(this.spaceRepo.activeSpaceCurrentRoomUid!);
    const session = this.spaceRepo.getSpace(sessionId);
    const oldChangeRoomsDialogOpen = this.dialog.openDialogs.find(
      (d) => d.componentInstance instanceof SpeedbumpModalComponent,
    );
    const [currentUserUpdated, roomChanged] = this.roomChangedForCurrentUser(session, update);
    if (!currentUserUpdated || currentRoomDeleted) {
      return true;
    }

    if (roomChanged) {
      const toJoinRoomId = update.data.sessionUsers.find(
        (u: SessionUser) => u._id === this.user?._id,
      )?.roomId;
      const currentRoomYJSID = this.spaceRepo.activeSpaceCurrentRoomUid;
      this.movedFromRoomID = this.spaceRepo.activeSpaceCurrentRoomUid ?? '';
      if (
        toJoinRoomId &&
        this.user?._id !== update.updatedBy?._id &&
        toJoinRoomId !== currentRoomYJSID
      ) {
        oldChangeRoomsDialogOpen?.componentInstance.dialogRef.close();
        applySessionUpdates &&= await this.showRoomChangedSpeedbump(toJoinRoomId);
      }
    } else {
      oldChangeRoomsDialogOpen?.componentInstance.dialogRef.close();
    }

    return applySessionUpdates;
  }

  private async showRoomChangedSpeedbump(toJoinRoomId: string) {
    const TIMEOUT = 30;

    const toJoinRoomName = this.spaceRepo.getSpaceRoomById(toJoinRoomId)?.name ?? 'new room';

    const titleEl = this.translate.instant('Joining Room');
    const timeoutText = this.translate.instant('You will join');
    const inText = this.translate.instant('in');
    const mainText = `${timeoutText} ${toJoinRoomName} ${inText} {timeout} {timeUnit}`;

    const joinText = this.translate.instant('Join now');

    const speedbumpData: SpeedbumpModalData = {
      title: titleEl,
      body: { main: mainText },
      controls: [
        {
          icon: 'exit_to_app',
          text: joinText,
          isSvg: true,
        },
      ],
      bodyMainTimeoutSeconds: TIMEOUT,
    };

    const ref = this.dialog.open(SpeedbumpModalComponent, {
      panelClass: 'speedbump-modal',
      disableClose: true,
      data: speedbumpData,
    });

    return firstValueFrom(
      ref.afterClosed().pipe(
        timeout(TIMEOUT * 1000),
        catchError(() => of(true)),
        tap(() => ref.close(true)),
        map((val) => !!val),
      ),
    );
  }

  private get user(): User | undefined {
    return this.userService.user.getValue()?.user;
  }

  private isIntendedForCurrentUser(
    intendedFor: CommonCollaboration.Realtime.SessionMessageAudience,
  ): boolean {
    const { SpaceUserRole } = CommonCollaboration.Spaces;
    const isHost = this.spaceRepo.isCurrentUserHost();

    // No role constraint means all roles match
    const roleMatches =
      !intendedFor.role || (intendedFor.role === SpaceUserRole.HOST ? isHost : !isHost);

    // No userIds constraint means all users match (?? true handles null/undefined)
    const userIdMatches = intendedFor.userIds?.includes(this.user?._id ?? '') ?? true;

    return roleMatches && userIdMatches;
  }

  private handleUserSpaceTransfer(update: RealtimeDataUpdate): void {
    const { intendedFor, transferredUser, toSpaceId } =
      update.data as CommonCollaboration.Realtime.UserSpaceTransferPayload;
    const { SpaceUserRole } = CommonCollaboration.Spaces;

    if (intendedFor.role === SpaceUserRole.HOST) {
      this.notifyHostOfAssignedStudent(transferredUser);
    } else {
      this.handleParticipantSpaceRedirect(toSpaceId);
    }
  }

  private notifyHostOfAssignedStudent(
    transferredUser: CommonCollaboration.Realtime.UserSpaceTransferPayload['transferredUser'],
  ): void {
    this.notificationToasterService.showDefaultWarningNotificationV2(
      this.translate.instant(`${transferredUser?.name} has been reassigned to your event`),
      '',
      this.notificationToasterService.generateNotificationCode(),
      { type: NotificationIconTypes.USER, user: transferredUser as User, size: 32 },
      undefined,
      30 * 1000,
    );
  }

  private handleParticipantSpaceRedirect(toSpaceId: string | undefined): void {
    if (!toSpaceId) {
      return;
    }

    const spinnerRef = this.dialog.open(SpinnerModalComponent, {
      data: {
        title: this.translate.instant("You're being reassigned"),
        description: this.translate.instant(
          'Your admin reassigned you to another Space. Please wait.',
        ),
      },
    });

    // 15 seconds before navigating to the new space
    const redirectDelayMs = 15 * 1000;
    modifiedSetTimeout(() => {
      spinnerRef.close();
      this.router.navigate(['/spaces', toSpaceId]);
    }, redirectDelayMs);
  }

  private handleSpaceSettingsUpdate(update: RealtimeDataUpdate) {
    const sessionId = update.id;
    const settings = update.data.settings;
    const session = this.spaceRepo.getSpace(sessionId);
    if (session) {
      session.settings = settings;
      this.spaceRepo.updateSpace(sessionId, session);
    }
  }

  private initializeChatListeners() {
    this.messagingService.initializePrivateChatList();
    this.messagesChannelSub = combineLatest([
      this.spaceRepo.activeSpace$.pipe(
        filterNil(),
        filter((activeSpace) => !!activeSpace.metadata?.general_chat_realtime_token),
        take(1),
      ),
      this.realtimeService.realtimeInitOrReconnect$,
    ]).subscribe(([activeSpace, reconnect]) => {
      const context = { session: activeSpace._id };
      if (!this.realtimeService.channelSubscrptionIsActive(context)) {
        this.realtimeService.subscribeMessages(
          context,
          activeSpace.metadata?.general_chat_realtime_token,
        );
      }
    });

    this.messageReceivedSub = merge(
      this.messagingService.newPrivateMessage$,
      this.messengerService.receivedSpaceLevelMessage$.pipe(
        filter(
          () =>
            !this.isChatOpened() ||
            (this.isChatOpened() &&
              this.messengerService.activeChatOptions.value !== ChatOptions.chat),
        ),
      ),
    ).subscribe(async (newMessage) => {
      if (newMessage) {
        await this.playMessageSound(newMessage);
      }
    });
  }

  private isChatOpened() {
    const currentPanelView = this.sessionSharedDataService.leftPanelView.getValue();
    return currentPanelView?.panelView === PanelView.chat && currentPanelView.expanded;
  }

  private async playMessageSound(newMessage: TypedMessage) {
    if (
      this.userService.user.getValue()?.user._id !== newMessage.author._id &&
      this.isChimesEnabled
    ) {
      this.debouncedNewMessageSoundPlay();
    }
  }

  private debouncedNewMessageSoundPlay = debounce(
    () => {
      const volume = this.flagsService.featureFlagsVariables[FLAGS.SPACES_USER_CHIMES]
        .chime_volume as number;

      this.audioService.playAudio(AudioChimes.newMessage, volume);
    },
    2000,
    { leading: true, trailing: false },
  );

  private isRoomDeleted(roomID: string): boolean {
    const isCurrRoomDeleted =
      this.spaceRepo.activeSpaceRooms.findIndex((room) => room.uid === roomID) === -1;
    return isCurrRoomDeleted;
  }

  setupListenersForRoomDeletion() {
    this.roomDeletionSub = this.spaceRepo.activeSpaceRooms$.subscribe((rooms) => {
      const oldChangeRoomsDialogOpen = this.dialog.openDialogs.find(
        (d) => d.componentInstance instanceof SpeedbumpModalComponent,
      );

      if (!oldChangeRoomsDialogOpen) {
        return;
      }

      if (this.movedFromRoomID && this.isRoomDeleted(this.movedFromRoomID)) {
        oldChangeRoomsDialogOpen?.componentInstance.dialogRef.close(true);
      }
    });
  }

  private setupRoomChangeChimeListener() {
    this.roomChangeChimeSub?.unsubscribe();

    this.roomChangeChimeSub = combineLatest([
      this.spaceRepo.activeSpaceId$.pipe(distinctUntilChanged()),
      this.spaceRepo.activeSpaceCurrentRoomUid$.pipe(distinctUntilChanged()),
      this.spaceRepo.hasActiveSpaceSynced$,
    ])
      .pipe(
        filter(([, , hasSynced]) => hasSynced),
        map(([spaceId, roomId]) => [spaceId, roomId] as [string | undefined, string | undefined]),
        pairwise(),
        filter(
          ([[prevSpaceId, prevRoom], [currentSpaceId, currentRoom]]) =>
            prevSpaceId === currentSpaceId &&
            prevRoom !== undefined &&
            currentRoom !== undefined &&
            prevRoom !== currentRoom &&
            this.isChimesEnabled,
        ),
      )
      .subscribe(() => {
        // Play chime when room changes (works for both user-initiated and host-initiated moves)
        this.audioService.playAudio(AudioChimes.userJoinsRoom);
      });
  }
}
